import {
  IconType,
  Heading,
  Button,
  Icon,
  InputName,
  InputText,
  Popover,
} from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { NAMESPACE } from 'locale'
import { selfServeConstants } from 'locale/selfServe'
import { Text } from '@lmc/ui-kit/src/general'
import { useDispatch, useSelector } from 'react-redux'
import { Carer, CarerRole, Home, Name } from '@lmc/ts-common'
import { FC, useState } from 'react'
import { Form } from 'antd'
import Operations from 'operations'
import { LogoutButton } from './LogoutButton'

export type Props = { next: () => void }

export const StepInvite: FC<Props> = ({ next }) => {
  const { t } = useTranslation(NAMESPACE.SELF_SERVE)

  const { t: tGlobal } = useTranslation()

  const home: Home | undefined = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const [loading, setLoading] = useState(false)

  const dispatch = useDispatch()

  const addCarer = (carer: Carer) =>
    dispatch(
      Operations.createListItem({
        listId: 'carers',
        itemId: '',
        json: carer,
      })
    )

  const isSelfServe = home.source === 'Self-serve'

  const [form] = Form.useForm()

  const handleSubmit = async ({
    firstname,
    lastname,
    email,
  }: {
    firstname: string
    lastname: string
    email: string
  }) => {
    await form.validateFields()
    setLoading(true)
    addCarer({
      name: { first: firstname, last: lastname } as Name,
      email,
      role: CarerRole.careHomeAdmin,
    } as Carer).then(() => {
      setLoading(false)
    })
  }

  return (
    <>
      <Icon
        customIcon={IconType.CleanLargeLogoIcon}
        style={{
          height: '100vh',
          width: 1000,
          position: 'absolute',
          right: 0,
          bottom: 0,
          top: '-20px',
        }}
      />
      <Wrapper>
        <TopWrapper>
          <Icon
            customIcon={IconType.LargeLogoIcon}
            style={{ height: 100, width: 250 }}
          />
          <LogoutButton />
        </TopWrapper>
        <CenterWrapper>
          <StyledHeading level={1}>
            {t(selfServeConstants.INVITE_TEAM)}
          </StyledHeading>
          <StyledText>{t(selfServeConstants.INVITE_TEAM_DESC)}</StyledText>
          <StyledTextLabel>
            {t(selfServeConstants.NAME_AND_EMAIL)}
          </StyledTextLabel>
          <Form form={form} onFinish={handleSubmit}>
            <InputNameStyled
              placeholder={{
                firstName: tGlobal(`onboarding.inputs.firstName`),
                lastName: tGlobal(`onboarding.inputs.lastName`),
              }}
            />
            <EmailWrapper>
              <InputStyled
                type="email"
                placeholder={tGlobal(`onboarding.inputs.email`)}
                required
                errorMessage={tGlobal(`onboarding.inputs.emailError`)}
                style={{ maxWidth: 418, width: '100%' }}
              />
            </EmailWrapper>
          </Form>
          <ButtonWrapper>
            <Popover content={t(selfServeConstants.PLEASE_FILL)}>
              <StyledButton
                variant="primary"
                size="large"
                onClick={form.submit}
                isLoading={loading}
              >
                {t(selfServeConstants.SEND_INVITE)}
              </StyledButton>
            </Popover>
            <StyledTextDesc>
              {t(selfServeConstants.STEP_X_OF_X, { from: 2, to: 3 })}
            </StyledTextDesc>
          </ButtonWrapper>
          {isSelfServe && (
            <WithoutWrapper onClick={next}>
              <StyledTextBottom level={1}>
                {t(selfServeConstants.CONTINUE_WITHOUT)}
              </StyledTextBottom>
            </WithoutWrapper>
          )}
        </CenterWrapper>
      </Wrapper>
    </>
  )
}

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.color.background.primary};
  height: 100vh;
  display: flex;
  flex-direction: column;
  padding: 100px 160px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    padding: 10px;
  }
`

const CenterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: auto;
  margin-top: auto;
`

const WithoutWrapper = styled.div`
  z-index: 10;
  margin-top: 30px;
`

const StyledTextDesc = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color11};
  margin-top: 5px;
  margin-left: 20px;
`

const StyledTextLabel = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color11};
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 20px;
`

const StyledTextBottom = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color11};
  text-decoration: underline;
  cursor: pointer;
`

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 30px;
`

const EmailWrapper = styled.div`
  margin-top: 15px;
`

const StyledHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color11};
  font-weight: 800;
  font-size: 54px;
  line-height: 59px;
  max-width: 800px;
  font-family: gilroy;
`

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color11};
  font-size: 24px;
  font-weight: 400;
  line-height: 32px;
  opacity: 0.7;
  max-width: 960px;
  margin-top: 10px;
  margin-bottom: 40px;
`

const StyledButton = styled(Button)`
  width: max-content;
  z-index: 10;
`

const InputNameStyled = styled(InputName)`
  .ant-input::placeholder {
    color: ${({ theme }) => theme.color.neutral.color5} !important;
  }
`

const InputStyled = styled(InputText)`
  .ant-input::placeholder {
    color: ${({ theme }) => theme.color.neutral.color5} !important;
  }
`
const TopWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`
